import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, Package, Radio, Wrench } from 'lucide-react';
import {
  addAccessoryToCart,
  formatAxiosAdminError,
  getAccessories,
  getAccessoryVitrine,
  type AccessoryVitrineSection,
  getGroupedProducts,
  getMotorHandbookCopy,
  getMotorHandbookMedia,
  getMotorsCatalog,
  getServices,
  getVitrineBranches,
  type VitrineBranchRow,
  resolveBackendMediaUrl,
} from '../api';
import { useAppStore } from '../store';
import { useNavigate, useLocation } from 'react-router-dom';
import { CatalogCategoryMedia } from '../components/CatalogCategoryMedia';
import { getCategoryMeta } from '../categoryAssets';
import type { Accessory, MotorCatalogItem, ProductGroup, Service } from '../types';
import AccessoriesTab from '../components/AccessoriesTab';
import { SkeletonCategoryGrid } from '../components/SkeletonCard';
import { useHaptic } from '../hooks/useHaptic';
import { OrderComposeBanner } from '../components/admin/OrderComposeBanner';
import { CatalogLegalFooter } from '../components/CatalogLegalFooter';
import { PROTOCOL_THEME } from '../protocolTheme';
import { normalizeCatalogCategoryLabel as normalizeCategoryLabel } from '../catalogTaxonomy';
import {
  PIM_BRANCH_UI,
  defaultPimGroupId,
  filterPimCatalogGroups,
  pimBranchKeyFromSlug,
  resolvePimGroupId,
  subtypeSlugFromGroupId,
} from '../lib/pimBranchUi';
import {
  pickRazdvizhnyeLeadGroup,
  RAZDVIZHNYE_DEFAULT_TIER,
} from '../lib/razdvizhnyeTier';

type Tab = 'catalog' | 'accessories' | 'motors' | 'services';
type ShowcasePreset = 'all' | 'smart' | 'battery' | 'premium';

/** Единый нормализатор строк grouped/v2 → стабильные массивы для UI (без дрейфа типов). */
function normalizeGroupedProductRows(data: ProductGroup[] | undefined): ProductGroup[] {
  return (data || []).map((g) => ({
    ...g,
    protocols: Array.isArray(g.protocols) ? g.protocols : [],
    interface_types: Array.isArray(g.interface_types) ? g.interface_types : [],
    variants: Array.isArray(g.variants) ? g.variants : [],
    advantages: Array.isArray(g.advantages) ? g.advantages : [],
  }));
}

/** Для сортировки и бейджей: полная матрица вариантов или счётчик с витрины v2. */
function variantsCountVisible(g: ProductGroup): number {
  if (Array.isArray(g.variants) && g.variants.length > 0) return g.variants.length;
  const n = g.variants_count;
  return n != null && Number.isFinite(Number(n)) ? Number(n) : 0;
}

/** Фон премиальной витрины: интерьер + блюр (картинки в репозитории). */
const CATALOG_HERO_AMBIENCE = '/images/products/rulonnye.jpg';

const springEase = [0.16, 1, 0.3, 1] as const;

export default function CatalogPage() {
  const [tab, setTab] = useState<Tab>('catalog');

  // Grouped catalog state
  const [groupedCatalog, setGroupedCatalog] = useState<ProductGroup[]>([]);
  const [catsLoading, setCatsLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState<string | null>(null);
  const [motorsView, setMotorsView] = useState(false);
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});
  const [activeGif, setActiveGif] = useState<string | null>(null);
  const [hoverCat, setHoverCat] = useState<string | null>(null);
  const [playingCat, setPlayingCat] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [showcasePreset, setShowcasePreset] = useState<ShowcasePreset>('all');

  // Accessories & services state
  const [accessories, setAccessories] = useState<Accessory[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [motorsCatalog, setMotorsCatalog] = useState<MotorCatalogItem[]>([]);
  const [motorHandbookMedia, setMotorHandbookMedia] = useState<Record<string, string>>({});
  const [motorHandbookCopy, setMotorHandbookCopy] = useState<Record<string, string>>({});
  const [extLoading, setExtLoading] = useState(false);
  const [accessoriesLoading, setAccessoriesLoading] = useState(false);
  const [accessoriesFetchError, setAccessoriesFetchError] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<Set<number>>(new Set());
  const [addingId, setAddingId] = useState<number | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const { token } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const haptic = useHaptic();
  const reduceMotion = useReducedMotion();
  /** После первой загрузки витрины повторные заходы на /catalog — фоновый refetch без скелетона (актуальные цены/позиции). */
  const catalogListHydrated = useRef(false);

  const [vitrineBranches, setVitrineBranches] = useState<VitrineBranchRow[]>([]);

  const [vitrineSections, setVitrineSections] = useState<AccessoryVitrineSection[]>([]);

  const reloadAccessories = useCallback(async () => {
    setAccessoriesLoading(true);
    setAccessoriesFetchError(null);
    try {
      const [rows, vitrine] = await Promise.all([
        getAccessories(),
        getAccessoryVitrine().catch(() => ({ sections: [] })),
      ]);
      setAccessories(Array.isArray(rows) ? rows : []);
      setVitrineSections(Array.isArray(vitrine?.sections) ? vitrine.sections : []);
    } catch (e) {
      console.error(e);
      setAccessoriesFetchError(
        formatAxiosAdminError(
          e,
          'Не удалось загрузить пульты и аксессуары. Проверьте интернет и что API доступен (тот же хост, что в VITE_BACKEND_URL).',
        ),
      );
    } finally {
      setAccessoriesLoading(false);
    }
  }, []);

  const handleAddAccessory = async (a: Accessory) => {
    if (!token) { navigate('/profile'); return; }
    setAddingId(a.id);
    try {
      await addAccessoryToCart(a.id);
      haptic.success();
      setAddedIds(prev => new Set(prev).add(a.id));
      setTimeout(() => setAddedIds(prev => { const n = new Set(prev); n.delete(a.id); return n; }), 2000);
    } catch { haptic.error(); }
    finally { setAddingId(null); }
  };

  useEffect(() => {
    const cat = searchParams.get('cat');
    if (cat) setSelectedCat(cat);
    const motorsParam = searchParams.get('motors');
    if (motorsParam === '1') {
      setTab('catalog');
      setSelectedCat(null);
      setMotorsView(true);
    }
    const tabParam = searchParams.get('tab');
    if (tabParam === 'catalog' || tabParam === 'accessories' || tabParam === 'services') {
      setTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (location.pathname !== '/catalog') return;
    const silent = catalogListHydrated.current;
    if (!silent) setCatsLoading(true);
    let cancelled = false;
    void getVitrineBranches()
      .then((rows) => {
        if (!cancelled) setVitrineBranches(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setVitrineBranches([]);
      });
    getGroupedProducts()
      .then((data) => {
        console.log('[CatalogPage] V3 API raw response:', data);
        
        if (cancelled) {
          return;
        }
        
        const normalized = normalizeGroupedProductRows(data);
        setGroupedCatalog(normalized);
        catalogListHydrated.current = true;
      })
      .catch((err) => {
        console.error('[CatalogPage] V3 API ERROR:', err);
      })
      .finally(() => {
        if (!cancelled) {
          setCatsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.key]);

  /** Свежий grouped по ветке (обходит 10‑мин кэш полного каталога на API). */
  useEffect(() => {
    if (!selectedCat || location.pathname !== '/catalog') return;
    const branch = vitrineBranches.find((b) => b.title === selectedCat);
    const filter = branch?.slug || selectedCat;
    let cancelled = false;
    void getGroupedProducts({ category: filter })
      .then((data) => {
        if (cancelled) return;
        const fresh = normalizeGroupedProductRows(data);
        if (fresh.length === 0) return;
        setGroupedCatalog((prev) => {
          const rest = prev.filter(
            (g) => g.category !== selectedCat && (!branch?.slug || g.branch_slug !== branch.slug),
          );
          return [...rest, ...fresh];
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [selectedCat, vitrineBranches, location.pathname]);

  // Poll for catalog updates every 8s while page is visible
  useEffect(() => {
    if (location.pathname !== '/catalog') return;
    const poll = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void getGroupedProducts().then((data) => {
        setGroupedCatalog(normalizeGroupedProductRows(data));
      }).catch(() => {});
    }, 8000);
    return () => clearInterval(poll);
  }, [location.pathname]);

  const categories = useMemo(() => {
    const isAccessoriesBranch = (b: VitrineBranchRow) => {
      const t = (b.title || '').toLowerCase();
      const s = (b.slug || '').toLowerCase();
      return t.includes('пульт') || s.includes('pulty') || s.includes('aks');
    };
    return vitrineBranches
      .filter((b) => b.show_on_vitrine && !isAccessoriesBranch(b))
      .sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title, 'ru'))
      .map((b) => ({
        raw: b.title,
        label: b.display_label || b.title,
        assetKey: normalizeCategoryLabel(b.title),
        sortRank: b.sort_order,
        slug: b.slug,
        hasContent: b.has_content,
        iconUrl: b.icon_url ? resolveBackendMediaUrl(b.icon_url) : undefined,
      }));
  }, [vitrineBranches]);

  useEffect(() => {
    if (!selectedCat || categories.length === 0) return;
    const exists = categories.some((c) => c.raw === selectedCat);
    if (!exists) setSelectedCat(null);
  }, [selectedCat, categories]);

  const selectedPimBranchKey = useMemo(() => {
    if (!selectedCat) return null;
    const branch = vitrineBranches.find((b) => b.title === selectedCat);
    return pimBranchKeyFromSlug(branch?.slug || selectedCat);
  }, [selectedCat, vitrineBranches]);

  const selectedCategoryItems = useMemo(() => {
    if (!selectedCat) return null;
    const branch = vitrineBranches.find((b) => b.title === selectedCat);
    const groups = groupedCatalog.filter(
      (g) =>
        g.category === selectedCat ||
        (branch?.slug && g.branch_slug === branch.slug),
    );
    const pimFiltered = filterPimCatalogGroups(branch?.slug, groups);
    const filtered = pimFiltered.filter((item) => {
      if (showcasePreset === 'all') return true;
      if (showcasePreset === 'smart')
        return (item.protocols || []).some((p) => ['ZigBee', 'Matter', 'Wi-Fi (MiHome)', 'Wi-Fi (TUYA)'].includes(p));
      if (showcasePreset === 'battery') return Boolean(item.has_battery);
      if (showcasePreset === 'premium') return Boolean(item.is_premium);
      return true;
    });
    return filtered.sort((a, b) => {
      const ao = a.subtype_sort_order ?? 9999;
      const bo = b.subtype_sort_order ?? 9999;
      if (ao !== bo) return ao - bo;
      return (a.name || '').localeCompare(b.name || '', 'ru');
    });
  }, [groupedCatalog, selectedCat, showcasePreset, vitrineBranches]);

  useEffect(() => {
    if (location.pathname !== '/catalog') return;
    if (tab !== 'accessories') return;
    void reloadAccessories();
  }, [location.pathname, tab, reloadAccessories]);

  /** Пока пользователь на «Товарах», подгружаем аксессуары в фоне — при переключении на «Пульты» цены уже в сторе. */
  useEffect(() => {
    if (location.pathname !== '/catalog') return;
    if (tab === 'accessories') return;
    if (accessories.length > 0) return;
    void reloadAccessories();
  }, [location.pathname, tab, accessories.length, reloadAccessories]);

  useEffect(() => {
    if (!(tab === 'accessories' || motorsView) || motorsCatalog.length > 0) return;
    const silentMotorsWarmup = tab === 'accessories' && !motorsView;
    if (!silentMotorsWarmup) setExtLoading(true);
    let cancelled = false;
    getMotorsCatalog()
      .then((rows) => {
        if (!cancelled) setMotorsCatalog(Array.isArray(rows) ? rows : []);
      })
      .catch(console.error)
      .finally(() => {
        if (!cancelled && !silentMotorsWarmup) setExtLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, motorsView, motorsCatalog.length]);

  useEffect(() => {
    if (!(tab === 'accessories' || motorsView)) return;
    void Promise.all([getMotorHandbookMedia(), getMotorHandbookCopy()])
      .then(([media, copy]) => {
        setMotorHandbookMedia(media || {});
        setMotorHandbookCopy(copy || {});
      })
      .catch(() => undefined);
  }, [tab, motorsView]);

  useEffect(() => {
    if (tab === 'services' && services.length === 0) {
      setExtLoading(true);
      getServices().then(setServices).catch(console.error).finally(() => setExtLoading(false));
    }
  }, [tab, services.length]);

  useEffect(() => {
    setIsSwitching(true);
    const timer = setTimeout(() => setIsSwitching(false), 180);
    return () => clearTimeout(timer);
  }, [selectedCat]);

  const showCategoryGrid = !selectedCat;
  const selectedCatLabel = selectedCat ? normalizeCategoryLabel(selectedCat) : '';

  const openLeadConfigurator = (categoryRaw: string, branchSlug?: string) => {
    if (branchSlug === 'motory' || categoryRaw === '__motors__') {
      setSelectedCat(null);
      setTab('catalog');
      setMotorsView(true);
      return;
    }
    setMotorsView(false);
    const candidates = groupedCatalog.filter(
      (g) =>
        g.category === categoryRaw ||
        (branchSlug && g.branch_slug === branchSlug),
    );
    if (!candidates.length) {
      setSelectedCat(categoryRaw);
      return;
    }
    const pimKey = pimBranchKeyFromSlug(branchSlug || categoryRaw);
    if (pimKey) {
      const cfg = PIM_BRANCH_UI[pimKey];
      const pimCandidates = candidates.filter((g) => {
        const gid = String(g.group_id || '');
        if (!gid.includes('::')) return false;
        const sub = subtypeSlugFromGroupId(gid);
        return cfg.subtypeSlugs.has(sub);
      });
      const rankedPim = [...pimCandidates].sort(
        (a, b) => variantsCountVisible(b) - variantsCountVisible(a),
      );
      for (const tile of cfg.navTiles) {
        const hit = rankedPim.find(
          (g) => subtypeSlugFromGroupId(String(g.group_id || '')) === tile.subtypeSlug,
        );
        if (hit?.group_id) {
          const gid = resolvePimGroupId(hit.group_id) || hit.group_id;
          navigate(`/product-group?id=${encodeURIComponent(gid)}`);
          return;
        }
      }
      if (rankedPim[0]?.group_id) {
        const gid = resolvePimGroupId(rankedPim[0].group_id) || rankedPim[0].group_id;
        navigate(`/product-group?id=${encodeURIComponent(gid)}`);
        return;
      }
      const fallbackGid = defaultPimGroupId(pimKey);
      if (fallbackGid) {
        navigate(`/product-group?id=${encodeURIComponent(fallbackGid)}`);
        return;
      }
    }
    const ranked = [...candidates].sort((a, b) => {
      const av = variantsCountVisible(a);
      const bv = variantsCountVisible(b);
      if (bv !== av) return bv - av;
      const ap = a.min_price ?? Number.MAX_SAFE_INTEGER;
      const bp = b.min_price ?? Number.MAX_SAFE_INTEGER;
      return ap - bp;
    });
    const bSlug = (branchSlug || '').toLowerCase();
    const isRazdvizhnye =
      bSlug.includes('razdvizh') || categoryRaw.toLowerCase().includes('раздвиж');
    const lead = isRazdvizhnye ? pickRazdvizhnyeLeadGroup(ranked) : ranked[0];
    if (lead?.group_id) {
      const tierQ = isRazdvizhnye ? `&tier=${encodeURIComponent(RAZDVIZHNYE_DEFAULT_TIER)}` : '';
      navigate(`/product-group?id=${encodeURIComponent(lead.group_id)}${tierQ}`);
      return;
    }
    setSelectedCat(categoryRaw);
  };

  const TABS = [
    { id: 'catalog' as Tab,     label: 'Товары',       icon: <Package size={14} /> },
    { id: 'accessories' as Tab, label: 'Пульты и аксессуары', icon: <Radio size={14} /> },
    { id: 'services' as Tab,    label: 'Услуги',        icon: <Wrench size={14} /> },
  ];

  const liquidHeroBackdrop =
    tab === 'catalog' && !selectedCat && !motorsView;

  return (
    <div className="flex flex-col min-h-screen">
      <OrderComposeBanner />

      {/* ── HEADER — эталон Liquid Glass / motion из 76b5ec5 (до ночных экспериментов со скроллом) ── */}
      <div
        className={`header-glass fixed top-0 left-0 right-0 z-50 pb-0 ${
          liquidHeroBackdrop ? 'overflow-hidden catalog-liquid-header' : ''
        }`}
      >
        {liquidHeroBackdrop && (
          <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
            <img
              src={CATALOG_HERO_AMBIENCE}
              alt=""
              className="absolute -top-6 left-1/2 w-[min(118%,880px)] -translate-x-1/2 h-40 md:h-48 object-cover opacity-[0.42]"
              style={{ filter: 'blur(40px)', transform: 'translateX(-50%) scale(1.1)' }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(10,25,47,0.22) 0%, rgba(10,25,47,0.72) 50%, rgba(10,25,47,0.9) 100%)',
              }}
            />
          </div>
        )}
        <div className="relative z-[1] px-4 pt-2.5 pb-0">
        {tab === 'catalog' && (selectedCat || motorsView) ? (
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => { haptic.impact('light'); setSelectedCat(null); setMotorsView(false); }}
              className="press p-1.5 -ml-1 rounded-xl"
              style={{ background: 'rgba(99,179,237,0.1)', border: '1px solid var(--border-glass)' }}
            >
              <ArrowLeft size={20} color="var(--cyan-bright)" />
            </button>
            <span className="text-base font-bold truncate" style={{ color: 'var(--text-1)' }}>
              {motorsView ? 'Моторы Onviz' : (selectedCatLabel || selectedCat)}
            </span>
          </div>
        ) : (
          <motion.div
            className="mb-2"
            initial={liquidHeroBackdrop && !reduceMotion ? { opacity: 0, y: 14 } : false}
            animate={liquidHeroBackdrop ? { opacity: 1, y: 0 } : undefined}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.52, ease: springEase }}
          >
            <img
              src="/logoon-white.svg"
              alt="Onviz"
              className="h-12 w-auto"
              style={{ objectFit: 'contain' }}
            />
          </motion.div>
        )}

        {/* Tab bar — layoutId: «перетекание» индикатора между вкладками */}
        <motion.div
          className="relative mt-0.5"
          initial={liquidHeroBackdrop && !reduceMotion ? { opacity: 0, y: 6 } : false}
          animate={liquidHeroBackdrop ? { opacity: 1, y: 0 } : undefined}
          transition={
            reduceMotion ? { duration: 0 } : { duration: 0.42, ease: springEase, delay: 0.08 }
          }
        >
          <LayoutGroup id="catalog-tabs">
            <div
              className="flex justify-center gap-0.5 overflow-x-auto no-scrollbar pb-px w-full"
              style={{ borderBottom: '1px solid var(--border-subtle)' }}
            >
              {TABS.map((tx) => {
                const active = tab === tx.id;
                return (
                  <button
                    key={tx.id}
                    type="button"
                    onClick={() => {
                      haptic.selection();
                      setTab(tx.id);
                    }}
                    className="relative shrink-0 min-w-[112px] sm:min-w-0 sm:flex-1 flex items-center justify-center gap-1.5 py-2.5 px-1.5 text-xs font-semibold uppercase transition-colors duration-200"
                    style={{
                      color: active ? 'var(--cyan-bright)' : 'var(--text-3)',
                      textShadow: active ? '0 0 14px var(--cyan-glow)' : 'none',
                    }}
                  >
                    {tx.icon} {tx.label}
                    {active && (
                      <motion.div
                        layoutId="catalog-tab-underline"
                        className="pointer-events-none absolute bottom-px left-[10%] right-[10%] h-[3px] rounded-full"
                        style={{
                          background:
                            'linear-gradient(90deg, rgba(56,189,248,0.95) 0%, rgba(147,219,252,1) 45%, rgba(99,179,237,1) 100%)',
                          boxShadow:
                            '0 0 14px rgba(99,179,237,0.65), 0 0 4px rgba(255,255,255,0.25) inset',
                        }}
                        transition={
                          reduceMotion
                            ? { duration: 0 }
                            : { type: 'spring', stiffness: 440, damping: 34, mass: 0.55 }
                        }
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </LayoutGroup>
        </motion.div>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div className="flex-1 overflow-y-auto pb-24 pt-32">
        <AnimatePresence mode="wait" initial={false}>
        {/* ── TAB: CATALOG ── */}
        {tab === 'catalog' && (
          <motion.div
            key="panel-catalog"
            className="min-h-0"
            initial={reduceMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -12, transition: { duration: 0.2, ease: springEase } }}
            transition={{ duration: reduceMotion ? 0 : 0.32, ease: springEase }}
          >
          <div className={`p-4 transition-all duration-200 ${isSwitching ? 'opacity-60 translate-y-1' : 'opacity-100 translate-y-0'}`}>

            {/* Level 1: Category grid */}
            {showCategoryGrid && !motorsView && (
              <>
              {catsLoading && <SkeletonCategoryGrid />}
              {/* Brand trust bar */}
              <motion.div
                className="glass rounded-2xl px-4 py-3 mb-4 flex items-center justify-between gap-2"
                initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                animate={catsLoading ? undefined : { opacity: 1, y: 0 }}
                transition={reduceMotion ? { duration: 0 } : { duration: 0.45, ease: springEase }}
              >
                {[
                  { val: '11 лет', sub: 'экспертизы' },
                  { val: '41 753', sub: 'реализованных проектов' },
                  { val: '70+', sub: 'городов присутствия' },
                  { val: '★ 5.0', sub: 'рейтинг сервиса' },
                ].map((s, i, arr) => (
                  <div key={s.val} className="contents">
                    <div className="text-center flex-1">
                      <p className="text-sm font-black" style={{ color: 'var(--cyan-bright)' }}>{s.val}</p>
                      <p className="text-[9px] leading-tight" style={{ color: 'var(--text-3)' }}>{s.sub}</p>
                    </div>
                    {i < arr.length - 1 && <div className="w-px h-6" style={{ background: 'var(--border-subtle)' }} />}
                  </div>
                ))}
              </motion.div>
              <motion.div
                className="grid grid-cols-2 gap-3 [&>*:last-child:nth-child(odd)]:col-span-2"
                initial="hidden"
                animate={catsLoading ? 'hidden' : 'show'}
                variants={{
                  hidden: {},
                  show: {
                    transition: reduceMotion
                      ? {}
                      : { staggerChildren: 0.035, delayChildren: 0.04 },
                  },
                }}
              >
                {categories.map((category, idx) => {
                  const cat = category.raw;
                  const catLabel = category.label;
                  const baseMeta = getCategoryMeta(
                    category.assetKey || catLabel || normalizeCategoryLabel(cat)
                  );
                  const meta = category.iconUrl
                    ? { ...baseMeta, image: category.iconUrl }
                    : baseMeta;
                  const isWide = categories.length % 2 !== 0 && idx === categories.length - 1;
                  const hasMedia = !!(meta.video || meta.gif);
                  const isActive = activeGif === cat;
                  const finePointer =
                    typeof window !== 'undefined'
                    && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
                  return (
                    <motion.button
                      type="button"
                      key={cat}
                      variants={{
                        hidden: { opacity: 0, y: 14, scale: 0.97 },
                        show: {
                          opacity: 1,
                          y: 0,
                          scale: 1,
                          transition:
                            reduceMotion ? { duration: 0 } : { duration: 0.28, ease: springEase },
                        },
                      }}
                      whileHover={reduceMotion || finePointer ? undefined : { scale: 1.02 }}
                      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
                      onClick={(e) => {
                        e.preventDefault();
                        if (hasMedia && !finePointer && activeGif !== cat) {
                          haptic.impact('light');
                          setActiveGif(cat);
                        } else {
                          haptic.impact('medium');
                          setActiveGif(null);
                          openLeadConfigurator(cat, category.slug);
                        }
                      }}
                      onMouseEnter={() => {
                        if (finePointer && meta.video) setHoverCat(cat);
                      }}
                      onMouseLeave={() => {
                        if (!finePointer) return;
                        setHoverCat((prev) => (prev === cat ? null : prev));
                        setPlayingCat((prev) => (prev === cat ? null : prev));
                      }}
                      className={`catalog-cat-card press relative rounded-2xl overflow-hidden ${
                        playingCat === cat ? 'catalog-cat-video-active' : ''
                      } ${
                        isWide ? 'h-32' : 'h-36'
                      }`}
                      style={{
                        border: '1px solid var(--border-glass)',
                        boxShadow: '0 8px 28px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
                      }}
                    >
                      <CatalogCategoryMedia
                        imageSrc={meta.image}
                        imageAlt={catLabel}
                        videoSrc={meta.video}
                        gifSrc={meta.gif}
                        imageError={Boolean(imgErrors[cat])}
                        touchActive={isActive && !finePointer}
                        hoverActive={finePointer && hoverCat === cat}
                        onImageError={() => setImgErrors((prev) => ({ ...prev, [cat]: true }))}
                        onVideoActiveChange={(on) => {
                          setPlayingCat((prev) => (on ? cat : prev === cat ? null : prev));
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent pointer-events-none" />
                      <div className="absolute bottom-0 left-0 right-0 p-3 text-left pointer-events-none">
                        <p className="text-white font-bold text-sm leading-tight">{catLabel}</p>
                      </div>
                    </motion.button>
                  );
                })}
              </motion.div>
              <motion.button
                type="button"
                onClick={() => {
                  haptic.selection();
                  navigate('/measurements?intent=measurement&source=catalog');
                }}
                className="press w-full mt-4 mb-1 rounded-2xl p-3 text-left"
                initial={reduceMotion ? false : { opacity: 0, y: 14 }}
                animate={catsLoading ? undefined : { opacity: 1, y: 0 }}
                transition={
                  reduceMotion ? { duration: 0 } : { duration: 0.48, ease: springEase, delay: 0.12 }
                }
                whileTap={reduceMotion ? undefined : { scale: 0.99 }}
                style={{
                  background: 'linear-gradient(135deg,#1a6eb5,#0e4a8c)',
                  border: '1px solid var(--cyan-border)',
                  boxShadow: '0 10px 32px rgba(26,110,181,0.35), inset 0 1px 0 rgba(255,255,255,0.12)',
                }}
              >
                <p className="text-[10px] font-bold uppercase tracking-wider text-white/80">
                  Персональный сервис Onviz
                </p>
                <p className="text-sm font-black mt-0.5 text-white">
                  Записаться на профессиональный замер
                </p>
                <p className="text-xs mt-1 text-white/80">
                  Технолог подготовит точную конфигурацию и персональное предложение под ваш интерьер
                </p>
              </motion.button>
              </>
            )}

            {/* Level 2: V2 positions inside category */}
            {motorsView && (
              <AccessoriesTab
                accessories={[]}
                motors={motorsCatalog}
                motorHandbookMedia={motorHandbookMedia}
                motorHandbookCopy={motorHandbookCopy}
                onMotorHandbookDataRefresh={(media, copy) => {
                  setMotorHandbookMedia(media);
                  setMotorHandbookCopy(copy);
                }}
                onOpenMotor={(groupId) => navigate(`/product-group?id=${encodeURIComponent(groupId)}&from=motors`)}
                extLoading={extLoading}
                addedIds={new Set<number>()}
                addingId={null}
                selectedGroup={null}
                setSelectedGroup={() => {}}
                handleAddAccessory={() => {}}
                motorsOnly
              />
            )}

            {!showCategoryGrid && !motorsView && (
              <>
                {!selectedCategoryItems ? (
                  <div className="flex flex-col items-center py-20 gap-2">
                    <span className="text-4xl opacity-40">🔍</span>
                    <p className="font-medium" style={{ color: 'var(--text-2)' }}>Нет решений в этой категории</p>
                  </div>
                ) : selectedCategoryItems.length === 0 ? (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center py-16 gap-2 px-4"
                  >
                    <span className="text-3xl opacity-40">◈</span>
                    {selectedPimBranchKey && showcasePreset === 'all' ? (
                      <>
                        <p className="text-sm text-center font-semibold" style={{ color: 'var(--text-2)' }}>
                          Каталог на сервере пуст для этой категории
                        </p>
                        <p className="text-xs text-center max-w-xs leading-relaxed" style={{ color: 'var(--text-3)' }}>
                          Импорт с Mac в Neon не виден Mini App, пока не выполнен bootstrap на Railway
                          (тот же DATABASE_URL, что у backend).
                        </p>
                      </>
                    ) : (
                      <p className="text-sm" style={{ color: 'var(--text-3)' }}>Нет решений по выбранному фильтру</p>
                    )}
                  </motion.div>
                ) : (
                  <div className="space-y-3">
                    {/* Filter pills */}
                    <div className="flex gap-2 overflow-x-auto pb-0.5">
                      {([
                        { id: 'all' as ShowcasePreset, label: 'Все', icon: '◈' },
                        { id: 'smart' as ShowcasePreset, label: 'Smart', icon: '📶' },
                        { id: 'battery' as ShowcasePreset, label: 'АКБ', icon: '🔋' },
                        { id: 'premium' as ShowcasePreset, label: 'Premium', icon: '★' },
                      ] as { id: ShowcasePreset; label: string; icon: string }[]).map((preset) => {
                        const active = showcasePreset === preset.id;
                        return (
                          <button key={preset.id} type="button"
                            className="press shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold"
                            style={active
                              ? { background: 'rgba(99,179,237,0.18)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }
                              : { background: 'rgba(255,255,255,0.04)', color: 'var(--text-3)', border: '1px solid var(--border-subtle)' }}
                            onClick={() => { haptic.selection(); setShowcasePreset(preset.id); }}
                          >
                            {preset.icon} {preset.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* Product cards — flat list, no subcategory wrappers */}
                    {selectedCategoryItems.map((item) => {
                      const itemProtocols = Array.isArray(item.protocols) ? item.protocols : [];
                      const itemInterfaces = Array.isArray(item.interface_types) ? item.interface_types : [];
                      const tags = [...itemProtocols, ...itemInterfaces].slice(0, 4);
                      const hasImg = !!item.image_url;
                      return (
                        <button
                          key={item.group_id}
                          type="button"
                          onClick={() => {
                            haptic.selection();
                            const gid = resolvePimGroupId(item.group_id) || item.group_id;
                            navigate(`/product-group?id=${encodeURIComponent(gid)}`);
                          }}
                          className="press w-full rounded-2xl overflow-hidden"
                          style={{ background: 'var(--glass-bg)', border: '1px solid var(--border-glass)', textAlign: 'left' }}
                        >
                          {/* Image banner */}
                          {hasImg && (
                            <div className="w-full h-36 overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)' }}>
                              <img
                                src={resolveBackendMediaUrl(item.image_url!)}
                                alt={item.name}
                                className="w-full h-full object-cover"
                                loading="lazy" decoding="async" referrerPolicy="no-referrer"
                              />
                            </div>
                          )}
                          <div className="p-3.5">
                            {/* Name + price row */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <p className="text-base font-bold leading-snug" style={{ color: 'var(--text-1)' }}>
                                {item.name}
                              </p>
                              {(item.min_price ?? 0) > 0 && (
                                <p className="text-base font-black shrink-0" style={{ color: 'var(--cyan-bright)' }}>
                                  от {(item.min_price!).toLocaleString('ru-RU')} ₽
                                </p>
                              )}
                            </div>
                            {/* Description */}
                            {(item.short_summary || item.short_description) && (
                              <p className="text-xs leading-relaxed line-clamp-2 mb-2" style={{ color: 'var(--text-2)' }}>
                                {item.short_summary || item.short_description}
                              </p>
                            )}
                            {/* Protocol + iface tags */}
                            {tags.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {tags.map((proto) => {
                                  const theme = PROTOCOL_THEME[proto];
                                  return (
                                    <span key={`${item.group_id}-${proto}`}
                                      className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                      style={theme
                                        ? { color: theme.text, background: theme.bg, border: `1px solid ${theme.border}` }
                                        : { color: 'var(--text-2)', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-subtle)' }}
                                    >
                                      {theme ? `${theme.icon} ${theme.label}` : proto}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
          </motion.div>
        )}

        {/* ── TAB: ACCESSORIES ── */}
        {tab === 'accessories' && (
          <motion.div
            key="panel-accessories"
            className="min-h-0"
            initial={reduceMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -12, transition: { duration: 0.2, ease: springEase } }}
            transition={{ duration: reduceMotion ? 0 : 0.32, ease: springEase }}
          >
          <AccessoriesTab
            accessories={accessories}
            vitrineSections={vitrineSections}
            motors={motorsCatalog}
            motorHandbookMedia={motorHandbookMedia}
            onOpenMotor={(groupId) => navigate(`/product-group?id=${encodeURIComponent(groupId)}&from=motors`)}
            extLoading={accessoriesLoading && accessories.length === 0}
            accessoriesFetchError={accessoriesFetchError}
            onRetryAccessoriesFetch={reloadAccessories}
            addedIds={addedIds}
            addingId={addingId}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            handleAddAccessory={handleAddAccessory}
          />
          </motion.div>
        )}

        {/* ── TAB: SERVICES ── */}
        {tab === 'services' && (
          <motion.div
            key="panel-services"
            className="min-h-0"
            initial={reduceMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -12, transition: { duration: 0.2, ease: springEase } }}
            transition={{ duration: reduceMotion ? 0 : 0.32, ease: springEase }}
          >
          <div className="p-4">
            {extLoading ? (
              <div className="flex justify-center py-20">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-2xl p-4 mb-4">
                  <p className="text-white/80 text-xs font-semibold uppercase tracking-wider">🔧 Доп. услуги</p>
                  <p className="text-white font-bold text-lg mt-0.5">Монтаж и сервисные опции</p>
                  <p className="text-white/70 text-xs mt-1">Профессиональный монтаж, адаптация и финальная настройка</p>
                </div>
                <p className="text-xs mb-3 px-1" style={{ color: 'var(--text-3)' }}>
                  {services.length} сервисных опций для комплектации и монтажа
                </p>
                <motion.div
                  className="space-y-2"
                  initial="hidden"
                  animate="show"
                  variants={{
                    hidden: {},
                    show: {
                      transition: { staggerChildren: 0.04, delayChildren: 0.06 },
                    },
                  }}
                >
                  {services.map((s) => (
                    <motion.div
                      key={s.id}
                      className="rounded-2xl px-4 py-3.5 flex items-center justify-between"
                      style={{ background: 'var(--glass-bg)', border: '1px solid var(--border-glass)' }}
                      variants={{
                        hidden: { opacity: 0, y: 14, scale: 0.98 },
                        show: {
                          opacity: 1,
                          y: 0,
                          scale: 1,
                          transition: { duration: 0.38, ease: springEase },
                        },
                      }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                          style={{ background: 'rgba(99,179,237,0.08)', border: '1px solid var(--border-subtle)' }}
                        >
                          <Wrench size={16} color="var(--cyan)" />
                        </div>
                        <p className="text-sm font-medium leading-snug" style={{ color: 'var(--text-1)' }}>{s.name}</p>
                      </div>
                      <p className="text-sm font-bold ml-3 shrink-0 whitespace-nowrap text-right" style={{ color: 'var(--cyan-bright)' }}>
                        {typeof s.price === 'number' ? `${s.price.toLocaleString('ru-RU')} ₽` : String(s.price)}
                      </p>
                    </motion.div>
                  ))}
                </motion.div>
              </>
            )}
          </div>
          </motion.div>
        )}
        </AnimatePresence>

        <CatalogLegalFooter />
      </div>
    </div>
  );
}
